/** WFID を必須にするカテゴリ（データを変更する＝WF 承認が要る）。 @graph-connects none */
export const WFID_REQUIRED_CATEGORIES = ['状態変更', 'データ修正'];
/** カテゴリが WF 承認（WFID）を要するデータ変更系か。フォームのプロアクティブ強調に使う（検証はブロックしない）。 @graph-connects none */
export function wfidRequiredFor(category) {
    return WFID_REQUIRED_CATEGORIES.includes(category);
}
/**
 * WFID を trim し、空文字は null（未入力）に畳む。起票（PIL フォーム / Channel.io `/ops`。後者は任意
 * パラメータなので未入力が空文字で届く）と編集が同じ正規化を通り、「未入力」が経路ごとに `''` と
 * `null` に分かれて保存されるのを防ぐ（表示も BQ 写像も null 判定を持つため、揺れると空欄が別物になる）。
 *
 * `undefined` を `??` で吸わない: 編集経路では `undefined` = 「キー未指定（更新しない）」で、`null` =
 * 「消去」とは別の意味を持つ。畳むと契約外の値が黙って消去に化けるので、型どおり `string | null` だけ受ける。
 * @graph-connects none
 */
export function normalizeOpsWfid(wfid) {
    if (wfid === null)
        return null;
    const trimmed = wfid.trim();
    return trimmed === '' ? null : trimmed;
}
/**
 * http(s) スキームの URL 文字列か（`new URL` の例外制御を避け、スキーム + 非空白で判定）。起票検証だけでなく、
 * ユーザー入力の URL を Backlog / 詳細ページのリンク（anchor href）に載せる前の**保存境界での再検証**にも使う
 * （`javascript:` 等のスキームを弾く単一 source。React は href の危険スキームを sanitize しないため）。
 * @graph-connects none
 */
export function isHttpUrl(value) {
    return /^https?:\/\/\S+$/i.test(value);
}
/** 自由記述の必須項目が満たすべき実質文字数（空白除く）。背景は 5W なので厚めに要求する。 @graph-connects none */
const MIN_BACKGROUND_CHARS = 12;
/** 対応依頼の最小実質文字数。 @graph-connects none */
const MIN_REQUESTED_CHARS = 8;
/** 中身のない定型文（そのまま出されがち）。実質的な記述を促すため弾く。 @graph-connects none */
const LOW_EFFORT_VALUES = new Set([
    'test',
    'testing',
    'asdf',
    'aaa',
    'na',
    'n/a',
    'なし',
    '特になし',
    '無し',
    'わからない',
    '不明',
]);
/**
 * 自由記述（背景 / 対応依頼）の低品質を LLM 無しで検出する。空・定型文・繰り返し文字（低多様性）・
 * 短すぎを弾き、問題があればエラーコードを、無ければ null を返す（文言は表示側が解決）。
 * @graph-connects none
 */
function freeTextIssue(value, minChars) {
    const trimmed = value.trim();
    if (trimmed === '')
        return { code: 'required' };
    if (LOW_EFFORT_VALUES.has(trimmed.toLowerCase()))
        return { code: 'lowEffort' };
    const compact = trimmed.replace(/\s/gu, '');
    if (new Set(compact).size <= 2)
        return { code: 'lowEffort' };
    if (compact.length < minChars)
        return { code: 'tooShort', params: { minChars } };
    return null;
}
/**
 * 新規依頼ドラフトを検証し、フィールド別エラーを返す（空オブジェクト = 妥当）。フォームの guided 表示と
 * API 側の再検証（gate）が同じ規則を使う。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 新規依頼ドラフトの必須・形式検証（User ID は数字・複数はカンマ区切り・自由記述の品質・URL 形式）を行いフィールド別エラーを返す（WFID は非ブロック）
 * @graph-connects none
 */
export function validateOpsRequestDraft(draft) {
    const errors = {};
    // null = N/A（検証しない）。[] = 未入力で required。非空は各 ID が数字であること（複数可）。
    if (draft.userIds !== null) {
        if (draft.userIds.length === 0)
            errors.userIds = { code: 'userIdsRequired' };
        else if (!draft.userIds.every(isOpsUserId))
            errors.userIds = { code: 'userIdsNotDigits' };
    }
    // WFID は非ブロック（送信は止めない）。データ変更カテゴリでの記入促しはフォーム側のプロアクティブ
    // 強調（wfidRequiredFor）で行い、検証エラーにはしない。
    const backgroundIssue = freeTextIssue(draft.background, MIN_BACKGROUND_CHARS);
    if (backgroundIssue !== null)
        errors.background = backgroundIssue;
    const requestedIssue = freeTextIssue(draft.requested, MIN_REQUESTED_CHARS);
    if (requestedIssue !== null)
        errors.requested = requestedIssue;
    const cs = (draft.csTicketUrl ?? '').trim();
    if (cs !== '' && !isHttpUrl(cs))
        errors.csTicketUrl = { code: 'invalidUrl' };
    return errors;
}
/**
 * エラーコードを既定の英語文言に変換する。locale を持たない文脈（API の ValidationError メッセージ・
 * ログ等）用。ユーザー向けフォームはこれを使わず i18n（react-i18next の t）でコードを解決する。
 * default を置かず、OpsFieldErrorCode に値を追加して case を書き忘れると noImplicitReturns
 * （node-library tsconfig）でコンパイルエラーになる＝網羅性をコンパイル時に強制する。
 * @graph-connects none
 */
export function formatOpsFieldError(error) {
    switch (error.code) {
        case 'userIdsRequired':
            return 'Enter one or more User IDs (comma-separated) or mark N/A';
        case 'userIdsNotDigits':
            return 'User IDs must be digits (comma-separated)';
        case 'required':
            return 'Required';
        case 'lowEffort':
            return 'Please write a real description';
        case 'tooShort':
            return `Too short — add more detail (min ${String(error.params?.minChars ?? '')} chars)`;
        case 'invalidUrl':
            return 'Enter a valid http(s) URL';
    }
}
/** ドラフトが妥当（エラーなし）か。 @graph-connects none */
export function isOpsRequestDraftValid(draft) {
    return Object.keys(validateOpsRequestDraft(draft)).length === 0;
}
/**
 * User ID 1 件が形式どおり（前後空白を除いて数字のみ）か。起票の検証と詳細編集（product-api）が同じ規則を
 * 使い、経路ごとに受け付ける ID が割れないようにする。
 * @graph-connects none
 */
export function isOpsUserId(id) {
    return /^\d+$/.test(id.trim());
}
/**
 * カンマ区切りの User ID 入力を trim + 非空の配列にする。フォーム入力・一覧フィルタ（match-any）・
 * BQ / Firestore の絞り込みで共通に使い、複数 User ID の分割ルールを 1 か所に集約する
 * （フォームが導き、各層が同じ規則で分割する）。
 * @graph-connects none
 */
export function parseUserIds(input) {
    return input
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '');
}
//# sourceMappingURL=submit.js.map